import { IconChevronRight } from "@tabler/icons-react";
import type { FC, ReactNode } from "react";
import { Link } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useLinkRender } from "../Link/routerLink.js";

export interface BreadcrumbItem {
  /** The crumb's text, or a component that resolves it (e.g. entity names). */
  label: ReactNode;
  href?: string;
  onClick?: () => void;
  isOverflowMarker?: boolean;
}

export interface BreadcrumbsProps {
  items: readonly BreadcrumbItem[];
  className?: string;
}

const interactiveItemClassName =
  "min-w-0 truncate text-xs leading-5 font-normal text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary";

/**
 * The breadcrumb trail for the page header: chevron-separated ancestors
 * leading up to the current page. Trails longer than four items collapse the
 * middle into an overflow marker, and the current item truncates first when
 * space runs out.
 */
export const Breadcrumbs: FC<BreadcrumbsProps> = ({ items, className }) => {
  const renderLink = useLinkRender();
  const visibleItems: readonly BreadcrumbItem[] =
    items.length > 4
      ? [
          ...items.slice(0, 1),
          { label: "...", isOverflowMarker: true },
          ...items.slice(-2),
        ]
      : items;

  return (
    <nav
      aria-label="Breadcrumb"
      className={twMerge("min-w-0 flex-1 overflow-hidden", className)}
    >
      <ol className="flex min-w-0 flex-nowrap items-center gap-x-2.5 overflow-hidden whitespace-nowrap">
        {visibleItems.map((item, index) => {
          const isCurrent = index === visibleItems.length - 1;
          const content = (
            <span
              className="truncate"
              aria-current={isCurrent ? "page" : undefined}
            >
              {item.label}
            </span>
          );

          return (
            <li
              key={index}
              className={twMerge(
                "flex min-w-0 items-center gap-2.5",
                isCurrent ? "shrink" : "shrink-0",
              )}
            >
              {index > 0 ? (
                <IconChevronRight
                  aria-hidden
                  className="size-3.5 shrink-0 text-muted-foreground"
                />
              ) : null}

              {item.isOverflowMarker ? (
                <span className="text-xs leading-5 font-normal text-muted-foreground">
                  ...
                </span>
              ) : item.href && !isCurrent ? (
                <Link
                  href={item.href}
                  className={interactiveItemClassName}
                  render={renderLink}
                >
                  {content}
                </Link>
              ) : item.onClick && !isCurrent ? (
                <button
                  type="button"
                  onClick={item.onClick}
                  className={twMerge(
                    interactiveItemClassName,
                    "cursor-pointer",
                  )}
                >
                  {content}
                </button>
              ) : (
                <span
                  className={twMerge(
                    "min-w-0 truncate text-xs leading-5 font-normal",
                    isCurrent ? "text-foreground" : "text-muted-foreground",
                  )}
                >
                  {content}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
